import { useTranslation } from 'react-i18next';
import { Check, ExternalLink, Info } from 'lucide-react';
import {
  Button,
  Checkbox,
  DialogClose,
  DialogFooter,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@scio3/ui';
import { DialogScrollBody } from '@/components/shared';
import { formatDateMedium } from '@/lib/format-date';
import { ScreenCountSelector } from '../components/ScreenCountSelector';
import { formatCurrencyAmount } from '../utils/plan-helpers';
import { OPTIDEV_MIN_LICENSES } from '../utils/billing-constants';
import {
  addedFromQuantity,
  maxAddableLicenses,
  minAddableLicenses,
  optidevBasePrice,
  optidevCreditsFor,
  optidevTrialDaysLeft,
  quantityFromAdded,
  type OptiDevAddonMode,
} from '../utils/optidev-addon';

type Props = {
  mode: OptiDevAddonMode;
  /** Current absolute license count — what gets submitted as `maxQuantity`. */
  quantity: number;
  onQuantityChange: (next: number) => void;
  /** The account's OptiSigns screen licenses — the ceiling for OptiDev licenses. */
  maxScreens: number;
  /** Screens already running OptiDev — the floor the backend enforces. */
  usedLicenses: number;
  enableAllScreens: boolean;
  onToggleAllScreens: (next: boolean) => void;
  /** Stripe tier-1 flat amount when available; undefined falls back to 3 × perScreen. */
  basePrice?: number;
  perScreenPrice: number;
  intervalKey: 'monthly' | 'yearly';
  currency: string;
  /**
   * Whether the trial is actually RUNNING — NOT derivable from `mode`, which
   * resolves a cancelled or expired trial to `subscribe` as well.
   */
  isOnTrial: boolean;
  trialEndDate?: string;
  helpLink?: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: () => void;
};

/**
 * OptiDev body + footer for the add-on dialog.
 *
 * Lives in its own component so every other add-on (AeriCast, Video Wall,
 * Background Music, Engage, Branding) keeps rendering AddonUpdateDialog's
 * original edit screen untouched — the dialog only swaps in this tree when
 * `addonKey === 'optidev'`.
 *
 * The add-on is presented as what the customer actually pays for — a monthly
 * AI-credit allowance — with screens as licenses that come with it, rather than
 * as a quantity of units. See `claude-dev/` design notes for the rationale.
 */
export function OptiDevAddonBody({
  mode,
  quantity,
  onQuantityChange,
  maxScreens,
  usedLicenses,
  enableAllScreens,
  onToggleAllScreens,
  basePrice,
  perScreenPrice,
  intervalKey,
  currency,
  isOnTrial,
  trialEndDate,
  helpLink,
  isSubmitting,
  onCancel,
  onSubmit,
}: Props) {
  const { t } = useTranslation();

  const isSubscribe = mode === 'subscribe';
  const isInfoOnly = mode === 'info';

  const added = addedFromQuantity(quantity);
  const maxAdd = maxAddableLicenses(maxScreens);
  const minAdd = minAddableLicenses(usedLicenses);
  // An account can hold more licenses than it has screens (screens removed
  // after purchase), so offer the control whenever there is headroom OR
  // something to give back.
  const canAddLicenses = maxAdd > 0 || added > 0;
  const totalLicenses = quantityFromAdded(added);
  const credits = optidevCreditsFor(quantity);

  const base = optidevBasePrice(basePrice, perScreenPrice);
  const addedCost = added * perScreenPrice;
  const total = base + addedCost;

  // Prices follow the billing interval; the credit allowance never does — an
  // annual subscriber still receives the same credits every month.
  const perScreenLabel = `${formatCurrencyAmount(perScreenPrice, currency)}/${
    intervalKey === 'monthly' ? t('mo') : t('yr')
  }`;
  const totalLabel = intervalKey === 'monthly' ? t('Total per month') : t('Total per year');

  // Gated on the trial actually running, NOT on `isSubscribe`: a cancelled or
  // expired trial resolves to `subscribe` too, and `trialEndDate` survives
  // cancellation — on an EARLY cancel it is still in the future, so the day
  // count alone would happily render "14 days left" for a dead trial.
  const showTrialStrip = isOnTrial && trialEndDate && optidevTrialDaysLeft(trialEndDate) > 0;
  const daysLeft = optidevTrialDaysLeft(trialEndDate);

  return (
    <>
      {/* Not a plain overflow-y-auto div: this lands the body on DialogContent's
          own gutter (a local px-6 would double it) and drives the footer shadow. */}
      <DialogScrollBody className="pt-6 pb-4 space-y-6">
        {showTrialStrip && (
          <div
            data-testid="optidev-trial-strip"
            className="rounded-md border border-warning/30 bg-warning/10 dark:bg-warning/20 p-4"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-semibold">{t('Free trial')}</span>
              <span className="text-sm font-semibold tabular-nums">
                {daysLeft === 1
                  ? t('{{count}} day left', { count: daysLeft })
                  : t('{{count}} days left', { count: daysLeft })}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('Ends {{date}} — the plan starts on its own then if you do nothing.', {
                date: formatDateMedium(trialEndDate),
              })}
            </p>
          </div>
        )}

        {/* Credits lead — they are the product. Deliberately no price hero: the
            base figure is not what the customer pays once licenses are added, so
            every price lives in the breakdown below instead. */}
        <div
          className="space-y-3 rounded-md border border-primary/20 bg-secondary p-4"
          data-testid="optidev-includes"
        >
          <IncludedRow
            title={t('{{count}} AI credits every month', { count: credits })}
            subtitle={t('To build & update your apps')}
          />
          {isSubscribe && (
            <IncludedRow
              title={t('Everything you built during the trial stays')}
              subtitle={t('Your projects and connections carry over untouched')}
            />
          )}
        </div>

        {/* Nothing to adjust means no control at all — an account that cannot
            add a license is shown a statement, not a dead stepper. */}
        {canAddLicenses && (
          <div className="space-y-4 pt-2" data-testid="optidev-licenses">
            <h4 className="text-sm font-semibold">{t('Add-on licenses')}</h4>
            {/* The offer while nothing is added, the arithmetic once it is. */}
            <p className="text-sm text-muted-foreground" data-testid="optidev-license-summary">
              {added === 0 ? (
                t('{{count}} are included. Add more for {{price}} each.', {
                  count: OPTIDEV_MIN_LICENSES,
                  price: perScreenLabel,
                })
              ) : (
                <>
                  <strong className="text-foreground">
                    {t('{{count}} licenses', { count: totalLicenses })}
                  </strong>
                  {' · '}
                  {t('{{included}} included + {{count}} added', {
                    included: OPTIDEV_MIN_LICENSES,
                    count: added,
                  })}
                </>
              )}
            </p>
            {/* Load-bearing: ScreenCountSelector's root is `items-center`, so it
                self-centres unless a shrink-to-fit flex parent left-aligns it. */}
            <div className="flex justify-start">
              <ScreenCountSelector
                value={added}
                onChange={(next) => onQuantityChange(quantityFromAdded(next))}
                min={minAdd}
                // Never clamp below what is already held — an over-provisioned
                // account must still be able to see and reduce its licenses.
                max={Math.max(maxAdd, added)}
                hideCircle
                disabled={enableAllScreens || isSubmitting}
              />
            </div>

            {/* Safe to nest here: `mode === 'update'` already implies
                canAddLicenses, so this can never be orphaned by the guard above. */}
            {mode === 'update' && (
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <Checkbox
                    checked={enableAllScreens}
                    onCheckedChange={(v) => onToggleAllScreens(v === true)}
                    disabled={isSubmitting}
                    aria-label={t('Enable for all screens')}
                  />
                  <span className="text-sm">{t('Enable for all screens')}</span>
                </label>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t('Enable for all screens details')}
                        className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center p-1 -m-1 shrink-0"
                      >
                        <Info className="h-4 w-4 pointer-events-none" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      {t(
                        'OptiDev add-on stays in sync with your OptiSigns screen subscription. Added or removed screens are billed automatically.',
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        )}

        {/* Line items. The amount taken at purchase is prorated onto the existing
            OptiSigns cycle, so the recurring total is the only figure that is
            true on this screen — the prorated charge belongs on the invoice. */}
        <div className="space-y-3" data-testid="optidev-breakdown">
          <BreakdownRow
            label={t('{{count}} AI credits / month', { count: credits })}
            sublabel={t('OptiDev Add-on')}
            amount={formatCurrencyAmount(base, currency)}
          />
          <BreakdownRow
            label={t('Add-on licenses')}
            sublabel={
              added === 0
                ? t('{{count}} included', { count: OPTIDEV_MIN_LICENSES })
                : t('{{included}} included + {{count}} added', {
                    included: OPTIDEV_MIN_LICENSES,
                    count: added,
                  })
            }
            amount={addedCost === 0 ? t('Free') : formatCurrencyAmount(addedCost, currency)}
          />
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-border">
            <span className="text-sm font-semibold">{totalLabel}</span>
            <span className="text-base font-semibold tabular-nums">
              {formatCurrencyAmount(total, currency)}
            </span>
          </div>
        </div>
      </DialogScrollBody>

      {/* An account that cannot change anything is shown a statement, not a form
          — so it gets no footer at all. */}
      {!isInfoOnly && (
        <DialogFooter className="sm:justify-between">
          {/* DialogClose routes through the dialog's onOpenChange, which keeps
              its isSubmitting guard. */}
          <DialogClose asChild>
            <Button variant="ghost" className="cursor-pointer">
              {t('Cancel')}
            </Button>
          </DialogClose>
          <div className="flex items-center gap-3">
            {helpLink && (
              <Button variant="link" asChild className="cursor-pointer underline">
                <a
                  href={helpLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onCancel}
                  className="inline-flex items-center gap-1"
                >
                  {t('Learn more')}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              </Button>
            )}
            <Button
              onClick={onSubmit}
              disabled={isSubmitting}
              data-testid="optidev-submit-btn"
              className={cn(isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')}
            >
              {isSubscribe ? t('Subscribe') : t('Update add-on')}
            </Button>
          </div>
        </DialogFooter>
      )}
    </>
  );
}

function IncludedRow({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="shrink-0 grid place-items-center rounded-full bg-primary text-primary-foreground h-5 w-5">
        <Check className="h-3 w-3" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{title}</span>
        <span className="block text-xs text-muted-foreground">{subtitle}</span>
      </span>
    </div>
  );
}

function BreakdownRow({
  label,
  sublabel,
  amount,
}: {
  label: string;
  sublabel: string;
  amount: string;
}) {
  // Muted amount: the Total below is the only full-weight number in the block.
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="min-w-0">
        <span className="block text-sm">{label}</span>
        <span className="block text-xs text-muted-foreground">{sublabel}</span>
      </span>
      <span className="text-sm font-medium tabular-nums shrink-0 text-muted-foreground">
        {amount}
      </span>
    </div>
  );
}
